import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
  toggleSidebarForWorkspace: (workspaceId?: string) => void;
  closeSidebar: () => void;
  closeSidebarForWorkspace: (workspaceId?: string) => void;
  openSidebar: () => void;
  closeSidebarIfMobile: () => void;
  syncSidebarForWorkspace: (workspaceId?: string, options?: { forceClosed?: boolean }) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebarContext must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: React.ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const getDefaultSidebarState = useCallback(() => {
    // サーバーサイドレンダリング時はPCサイズと仮定
    if (typeof window === 'undefined') {
      return true;
    }
    // スマホサイズ（768px以下）では初期状態を閉じる
    const isMobileSize = window.innerWidth <= 768;
    return !isMobileSize;
  }, []);

  const [isOpen, setIsOpen] = useState(getDefaultSidebarState);
  const [isMobile, setIsMobile] = useState(false);
  const [workspaceSidebarPreferences, setWorkspaceSidebarPreferences] = useState<Record<string, boolean>>({});
  const lastActionRef = useRef<string>('');

  const getWorkspaceSidebarPreference = useCallback((workspaceId?: string) => {
    if (!workspaceId) {
      return getDefaultSidebarState();
    }

    return workspaceSidebarPreferences[workspaceId] ?? getDefaultSidebarState();
  }, [getDefaultSidebarState, workspaceSidebarPreferences]);

  // 画面サイズの変更を監視
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;

      setIsMobile(mobile);

      // スマホサイズになった時にサイドバーを閉じる
      if (mobile && isOpen) {
        setIsOpen(false);
      }
    };

    // 初期化
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // isOpenを依存関係から削除

  const toggleSidebar = useCallback(() => {
    lastActionRef.current = 'toggle';
    setIsOpen(!isOpen);
  }, [isOpen]);

  const toggleSidebarForWorkspace = useCallback((workspaceId?: string) => {
    const nextIsOpen = !getWorkspaceSidebarPreference(workspaceId);
    lastActionRef.current = 'toggle';

    if (workspaceId) {
      setWorkspaceSidebarPreferences((prev) => ({
        ...prev,
        [workspaceId]: nextIsOpen,
      }));
    }

    setIsOpen(nextIsOpen);
  }, [getWorkspaceSidebarPreference]);

  const closeSidebar = useCallback(() => {
    lastActionRef.current = 'close';
    setIsOpen(false);
  }, []);

  const closeSidebarForWorkspace = useCallback((workspaceId?: string) => {
    lastActionRef.current = 'close';
    if (workspaceId) {
      setWorkspaceSidebarPreferences((prev) => ({
        ...prev,
        [workspaceId]: false,
      }));
    }
    setIsOpen(false);
  }, []);

  const openSidebar = useCallback(() => {
    lastActionRef.current = 'open';
    setIsOpen(true);
  }, []);

  const syncSidebarForWorkspace = useCallback((workspaceId?: string, options?: { forceClosed?: boolean }) => {
    if (options?.forceClosed) {
      setIsOpen(false);
      return;
    }

    setIsOpen(getWorkspaceSidebarPreference(workspaceId));
  }, [getWorkspaceSidebarPreference]);

  // スマホの場合のみサイドバーを閉じる（選択が切り替わった時）
  const closeSidebarIfMobile = useCallback(() => {
    if (isMobile) {
      // 少し遅延させてから閉じる（選択処理が完了してから）
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    }
  }, [isMobile]);

  return (
    <SidebarContext.Provider value={{ 
      isOpen, 
      isMobile, 
      toggleSidebar, 
      toggleSidebarForWorkspace,
      closeSidebar,
      closeSidebarForWorkspace, 
      openSidebar, 
      closeSidebarIfMobile,
      syncSidebarForWorkspace,
    }}>
      {children}
    </SidebarContext.Provider>
  );
}; 